/**
 * Mail-Away Shipment Details — per-jurisdiction shipment tracking card.
 *
 * Surfaced inside the MLC & Workflow tab of JurisdictionManager when the
 * order has a matching Mail-Away request for the current jurisdiction
 * (i.e. once the order has entered mail_away status — retroactively
 * editable thereafter).
 *
 * Editable by admin + tax_search_team_leader only. Other roles see a
 * compact read-only summary.
 *
 * All fields are optional — partial saves allowed.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Truck, Copy, Check, Save } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const EDITABLE_ROLES = new Set(['admin', 'tax_search_team_leader']);
const CARRIER_OPTIONS = ['FedEx', 'UPS', 'USPS', 'Other'];

// Convert ISO datetime → "YYYY-MM-DD" for <input type="date" />
const isoToDateInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const MailAwayShipmentDetails = ({ orderId, mailAwayRequest, onSaved }) => {
  const { user, token } = useAuth();
  const canEdit = useMemo(
    () => user?.role && EDITABLE_ROLES.has(String(user.role)),
    [user]
  );

  // `seed` is derived from the prop (latest server-known values for THIS render).
  // After a successful save, we additionally remember the just-persisted values
  // in `localBaseline` so the dirty-check resets to false even before the parent
  // prop refresh round-trips back. Cleared whenever the prop itself updates.
  const seed = useMemo(() => ({
    tracking_number: mailAwayRequest?.shipment_tracking_number || '',
    carrier: mailAwayRequest?.shipment_carrier || '',
    check_number: mailAwayRequest?.shipment_check_number || '',
    check_amount: mailAwayRequest?.shipment_check_amount ?? '',
    mailed_date: isoToDateInput(mailAwayRequest?.shipment_mailed_date),
    return_tracking_number: mailAwayRequest?.shipment_return_tracking_number || '',
    notes: mailAwayRequest?.shipment_notes || '',
  }), [mailAwayRequest]);

  const [form, setForm] = useState(seed);
  const [localBaseline, setLocalBaseline] = useState(null);
  const [lastSavedMeta, setLastSavedMeta] = useState(null); // {updated_at, updated_by_name}
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Re-seed (and clear any local baseline/saved meta) when the underlying prop
  // changes, e.g. parent refetches the order after another update.
  useEffect(() => {
    setForm(seed);
    setLocalBaseline(null);
    setLastSavedMeta(null);
  }, [seed]);

  // Effective baseline used for dirty-check + outgoing-payload diffing.
  const effectiveSeed = localBaseline || seed;

  const dirty = useMemo(() => {
    return Object.keys(effectiveSeed).some(k => String(effectiveSeed[k] ?? '') !== String(form[k] ?? ''));
  }, [effectiveSeed, form]);

  const handleChange = (field) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCopy = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      // Send only changed fields (vs. effectiveSeed) so backend exclude_unset
      // works correctly.
      const body = {};
      Object.keys(effectiveSeed).forEach(k => {
        if (String(effectiveSeed[k] ?? '') !== String(form[k] ?? '')) {
          if (k === 'check_amount') {
            body[k] = form[k] === '' || form[k] === null ? null : Number(form[k]);
          } else if (k === 'mailed_date') {
            body[k] = form[k] || null;
          } else {
            body[k] = form[k];
          }
        }
      });

      if (Object.keys(body).length === 0) {
        toast.info('No changes to save');
        return;
      }

      const url = `${API}/api/orders/${orderId}/mail-away-requests/${mailAwayRequest.request_id}/shipment`;
      const { data } = await axios.patch(url, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Re-baseline from the server echo so the Save button immediately
      // disables until the user makes another change.
      const updated = data?.request;
      if (updated) {
        const newBaseline = {
          tracking_number: updated.shipment_tracking_number || '',
          carrier: updated.shipment_carrier || '',
          check_number: updated.shipment_check_number || '',
          check_amount: updated.shipment_check_amount ?? '',
          mailed_date: isoToDateInput(updated.shipment_mailed_date),
          return_tracking_number: updated.shipment_return_tracking_number || '',
          notes: updated.shipment_notes || '',
        };
        setLocalBaseline(newBaseline);
        setForm(newBaseline);
        setLastSavedMeta({
          updated_at: updated.shipment_updated_at,
          updated_by_name: updated.shipment_updated_by_name,
        });
      }

      toast.success(data?.message || 'Shipment details saved');
      if (onSaved) onSaved(data?.request);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Save failed';
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  if (!mailAwayRequest) return null;

  const jurisdictionLabel =
    mailAwayRequest.jurisdiction_name +
    (mailAwayRequest.jurisdiction_type ? ` (${mailAwayRequest.jurisdiction_type})` : '');

  return (
    <div
      className="space-y-3 rounded-lg border border-blue-100 bg-blue-50/30 p-4"
      data-testid={`mail-away-shipment-card-${mailAwayRequest.request_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Truck className="w-4 h-4 text-blue-600" />
          Mail-Away Shipment Details
          <span className="text-xs font-normal text-slate-500">— {jurisdictionLabel}</span>
        </h3>
        {!canEdit && (
          <span className="text-xs text-slate-400 italic">View only</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Tracking Number */}
        <div>
          <Label className="text-xs text-slate-600 mb-1 block font-medium">Tracking Number</Label>
          <div className="relative">
            <Input
              value={form.tracking_number}
              onChange={handleChange('tracking_number')}
              placeholder="e.g. 1Z999AA10123456784"
              className="h-9 pr-9"
              readOnly={!canEdit}
              data-testid="shipment-tracking-number"
            />
            {form.tracking_number && (
              <button
                type="button"
                onClick={() => handleCopy('tracking', form.tracking_number)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-500"
                title="Copy tracking number"
                data-testid="copy-tracking-number"
              >
                {copiedField === 'tracking'
                  ? <Check className="w-3.5 h-3.5 text-green-600" />
                  : <Copy className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>

        {/* Carrier */}
        <div>
          <Label className="text-xs text-slate-600 mb-1 block font-medium">Carrier</Label>
          {canEdit ? (
            <Select
              value={form.carrier || ''}
              onValueChange={handleChange('carrier')}
            >
              <SelectTrigger className="h-9" data-testid="shipment-carrier">
                <SelectValue placeholder="Select carrier…" />
              </SelectTrigger>
              <SelectContent>
                {CARRIER_OPTIONS.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={form.carrier || ''}
              readOnly
              className="h-9 bg-white cursor-default select-text"
              data-testid="shipment-carrier"
            />
          )}
        </div>

        {/* Check Number */}
        <div>
          <Label className="text-xs text-slate-600 mb-1 block font-medium">Check Number</Label>
          <Input
            value={form.check_number}
            onChange={handleChange('check_number')}
            placeholder="e.g. 4521"
            className="h-9"
            readOnly={!canEdit}
            data-testid="shipment-check-number"
          />
        </div>

        {/* Check Amount */}
        <div>
          <Label className="text-xs text-slate-600 mb-1 block font-medium">Check Amount ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.check_amount}
            onChange={handleChange('check_amount')}
            placeholder="0.00"
            className="h-9"
            readOnly={!canEdit}
            data-testid="shipment-check-amount"
          />
        </div>

        {/* Mailed Date */}
        <div>
          <Label className="text-xs text-slate-600 mb-1 block font-medium">Mailed Date</Label>
          <Input
            type="date"
            value={form.mailed_date}
            onChange={handleChange('mailed_date')}
            className="h-9"
            readOnly={!canEdit}
            data-testid="shipment-mailed-date"
          />
        </div>

        {/* Return Tracking Number */}
        <div>
          <Label className="text-xs text-slate-600 mb-1 block font-medium">Return Tracking #</Label>
          <div className="relative">
            <Input
              value={form.return_tracking_number}
              onChange={handleChange('return_tracking_number')}
              placeholder="If applicable"
              className="h-9 pr-9"
              readOnly={!canEdit}
              data-testid="shipment-return-tracking-number"
            />
            {form.return_tracking_number && (
              <button
                type="button"
                onClick={() => handleCopy('return', form.return_tracking_number)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-500"
                title="Copy return tracking number"
                data-testid="copy-return-tracking-number"
              >
                {copiedField === 'return'
                  ? <Check className="w-3.5 h-3.5 text-green-600" />
                  : <Copy className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notes — full width */}
      <div>
        <Label className="text-xs text-slate-600 mb-1 block font-medium">Additional Notes</Label>
        <Textarea
          value={form.notes}
          onChange={handleChange('notes')}
          placeholder="Internal notes about this shipment"
          rows={2}
          className="text-sm resize-none"
          readOnly={!canEdit}
          data-testid="shipment-notes"
        />
      </div>

      {/* Footer: last-updated meta + Save */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="text-[11px] text-slate-500" data-testid="shipment-last-updated-meta">
          {(() => {
            const ts = lastSavedMeta?.updated_at || mailAwayRequest.shipment_updated_at;
            const by = lastSavedMeta?.updated_by_name || mailAwayRequest.shipment_updated_by_name;
            if (ts) {
              return (
                <>
                  Last updated {formatDateTimeLocal(ts)}{' '}
                  {by ? `by ${by}` : ''}
                </>
              );
            }
            return <span className="italic">No shipment details recorded yet</span>;
          })()}
        </div>
        {canEdit && (
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid="shipment-save-button"
            className="h-8"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default MailAwayShipmentDetails;
